/* Resolving a key to a string, and the fallback chain.
 *
 * Deliberately tiny. The value is not in the machinery — it is in there being
 * exactly one place a user-facing string can come from.
 */
import { IntlMessageFormat } from 'intl-messageformat';
import { CATALOGUES, LOCALES, SOURCE_LOCALE } from './catalogue.js';
/** `specific → base → source`, per `i18n.md`. `tr-TR` and `tr-CY` both give `tr`. */
export function resolveLocale(requested) {
    if (!requested)
        return SOURCE_LOCALE;
    /* Lowercased with an explicit locale. Under a Turkish default,
     * `'I'.toLowerCase()` is `'ı'` and the tag would not match — `i18n.md` calls
     * any casing without an explicit locale a defect, and this is exactly the
     * place it bites. */
    const base = requested.toLocaleLowerCase('en-US').split('-')[0] ?? '';
    return LOCALES.find((locale) => locale === base) ?? SOURCE_LOCALE;
}
export class MissingMessageError extends Error {
    constructor(key, locale) {
        super(`No message for "${key}" in "${locale}".`);
        this.name = 'MissingMessageError';
    }
}
/**
 * Builds a translator for one locale.
 *
 * A key missing from a TARGET locale falls back to the source and is reported.
 * A key missing from the SOURCE throws: `i18n.md` requires that to fail the
 * build rather than degrade silently in a way nobody notices, and the key type
 * makes it unreachable from TypeScript in the first place.
 */
export function createTranslator(locale, onMissing) {
    const catalogue = CATALOGUES[locale];
    const source = CATALOGUES[SOURCE_LOCALE];
    return (key, values) => {
        const template = catalogue[key] ?? source[key];
        if (template === undefined)
            throw new MissingMessageError(key, locale);
        if (catalogue[key] === undefined)
            onMissing?.(key, locale);
        /* Only formatted when there is something to substitute. A message with no
         * arguments is returned as written, which keeps a literal brace in copy
         * from being read as ICU syntax by a message that never meant it. */
        return values ? interpolate(template, values, locale) : template;
    };
}
/**
 * ICU MessageFormat, from STORY-016 onward.
 *
 * The catalogue did not change shape when this arrived, and that was the point
 * of writing the placeholder syntax as ICU's from the start: named arguments
 * `{email}` rather than positional `{0}`, because a translator cannot reorder a
 * positional argument and word order is exactly what changes between languages.
 *
 * What it buys is plurals, which is why STORY-016 is the trigger — "40 guests,
 * 66 people" is the first message with a count in it. **An `if (count === 1)`
 * branch stays forbidden**: English has two plural forms, Turkish applies a
 * different rule again, and Arabic has six. Only CLDR knows, and this is how it
 * gets asked.
 *
 * Every message already shipped was checked before the swap for the three
 * things ICU reads differently — an ASCII apostrophe, a brace that is not a
 * placeholder, and `#`. There were none, which is why this was a change of one
 * function rather than of the catalogue.
 */
const FORMATTERS = new Map();
function formatterFor(locale, template) {
    /* A separator no message contains, so a locale and a template cannot collide
     * by concatenation. Written as an escape rather than a literal NUL: a real
     * one makes git treat the file as binary, which happened once already in
     * `timezones.ts`.
     *
     * Parsing is the expensive half of ICU and these render repeatedly — a guest
     * list formats one message once per row. */
    const cacheKey = `${locale}\u0000${template}`;
    const cached = FORMATTERS.get(cacheKey);
    if (cached)
        return cached;
    const formatter = new IntlMessageFormat(template, locale);
    FORMATTERS.set(cacheKey, formatter);
    return formatter;
}
/**
 * Renders one message.
 *
 * Exported within the package so the pseudo-locale substitutes the same way the
 * real translator does. A second implementation would drift, and the one place
 * it drifted would be the checker nobody re-checks.
 */
export function interpolate(template, values, locale = SOURCE_LOCALE) {
    try {
        /* `format` answers a string for a plain message and can answer an array
         * when one carries rich-text tags. Nothing here uses those, and joining is
         * a truer answer than a cast that would be wrong the day somebody does. */
        const formatted = formatterFor(locale, template).format(values);
        return Array.isArray(formatted) ? formatted.join('') : String(formatted);
    }
    catch {
        /**
         * A missing argument leaves the placeholder visible, as it did before ICU.
         *
         * This is a deliberate restoration rather than a swallowed error. The old
         * formatter left an unsubstituted `{placeholder}` on screen on the stated
         * reasoning that a missing value should look wrong in review rather than
         * quietly produce a sentence with a hole in it. `IntlMessageFormat` throws
         * `MISSING_VALUE` instead — inside React's render, where the route error
         * boundary catches it and blanks the whole screen.
         *
         * Blanking a screen over one absent interpolation is a worse failure than
         * the one it replaced: it hides which message was wrong and takes the rest
         * of the page with it. So the raw template is returned, the hole is
         * visible, and the defect stays local to the sentence that has it.
         *
         * The pseudo-locale sweep still sees the template as translated, so this
         * does not create a silent gap in that check — what it creates is a screen
         * that renders with an obvious hole rather than not at all.
         */
        return template;
    }
}
//# sourceMappingURL=translate.js.map